import * as React from 'react'
import styled, { css } from 'styled-components'

import * as S from '../../../styles'
import * as colors from '../../../constants/colors'

import { Field } from './Field'
import { ControlledRadioInput, styleRadioToggle } from '../radio/RadioInput'
import { radioGroupContext } from './RadioGroupContext'

export type RadioFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  isError?: boolean
  className?: string
  id?: string
  label?: React.ReactNode
  description?: string
  context?: string
  children?: React.ReactNode
}

function RadioFieldComponent({
  className,
  isError = false,
  label,
  description,
  id = String(Math.random()),
  children,
  context,
  ...inputProps
}: RadioFieldProps): React.ReactElement<RadioFieldProps> {
  const groupContext = React.useContext(radioGroupContext)
  let type: 'small' | 'medium' | 'big' = 'small'
  if (groupContext !== undefined) {
    inputProps.name = inputProps.name || groupContext.name
    type = groupContext.type || type
  }

  return (
    <RadioFieldWrapper className={className} type={type}>
      {children}
      <S.input.HiddenFocusableInput type="radio" {...inputProps} id={id} />
      <StyledRadioField isError={isError} htmlFor={id} label={label} description={description} context={context}>
        <ControlledRadioInput disabled={inputProps.disabled} />
      </StyledRadioField>
    </RadioFieldWrapper>
  )
}

/** extends `<RadioInput>`, see there for additional props. Usually used with `<RadioGroupField>`. */
export const RadioField = styled(RadioFieldComponent)<RadioFieldProps>``
RadioField.displayName = 'RadioField'

export const StyledRadioField = styled(Field)<{ isError?: boolean }>`
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 2px;
  border: ${({ isError }) =>
    isError ? `1px dashed ${colors.PRIMARY_RED_500}` : `1px solid ${colors.PRIMARY_SAND_200}`};
  justify-content: center;
  padding: 12px 12px 12px 40px;
  position: relative;

  ${S.field.Status} {
    width: 100%;
  }

  ${S.field.Title} {
    margin: 0px !important;
  }

  ${S.input.HiddenFocusableInput}:focus ~ & {
    border-color: ${({ isError }) => (isError ? colors.PRIMARY_RED_500 : colors.PRIMARY_GREY_500)};
  }

  ${S.input.HiddenFocusableInput}:checked ~ & {
    border-color: ${colors.PRIMARY_BLUE_500};
    border-style: solid;

    ${S.field.Title} {
      color: ${colors.PRIMARY_BLUE_500};
    }
  }

  ${S.input.HiddenFocusableInput}:disabled ~ & {
    border-color: ${colors.SECONDARY_GREY_400};
    border-style: solid;
    background-color: ${colors.SECONDARY_GREY_400};

    ${S.field.Title} {
      margin: 0px;
    }
  }
`

export const RadioFieldWrapper = styled.div<{ type: 'small' | 'medium' | 'big' }>`
  position: relative;

  ${S.input.HiddenFocusableInput} {
    ${({ type }) => {
      if (type === 'big' || type === 'medium') {
        return css`
          height: 100%;
          left: 0;
          line-height: 78px;
          padding-left: 12px;
          width: 100%;

          & + ${StyledRadioField} {
            ${S.field.Title} {
              ${S.copy._smallStyle};
              display: block;
              font-weight: 600; /* HACK: Irregular font manipulation. We'll be adding copy.S.semiBold, so this is just by anticipation. */
            }
          }
        `
      } else {
        return css`
          // center over fake radio
          height: 18px;
          left: -5px;
          margin-top: 1px;
          top: 50%;
          transform: translateY(-50%);
          width: 18px;

          & + ${StyledRadioField} {
            ${S.field.Title} {
              display: block;
              ${S.copy._smallStyle};
            }
          }
        `
      }
    }}
  }

  ${ControlledRadioInput} {
    position: absolute;

    ${({ type }) => {
      if (type === 'small') {
        return css`
          left: 0;
        `
      } else {
        return css`
          left: 10px;
        `
      }
    }}

    /*
      We want <RadioInput> and <S.input.HiddenFocusableInput> to be supperposed (click on <RadioInput> = click on <S.input.HiddenFocusableInput> = check the radio control)
      We want <RadioInput> left to <StyledRadioField> and vertically centered.
      This could be done with flex positioning but :
        - we would need to have <RadioInput> as as sibling of <StyledRadioField> (impossible given <Field> definition)
        - it would be difficult to have <S.input.HiddenFocusableInput> over <RadioInput> unless they where child of the same parent (impossible because we need <S.input.HiddenFocusableInput> as sibling of <StyledRadioField> for css selector shenaningans for :checked styling).
      This leaves us  with top: 50% + translate as last resort for the vertical centering.
    */
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
  }

  ${styleRadioToggle(S.input.HiddenFocusableInput, StyledRadioField, {
    checked: css`
      position: relative;
      z-index: 1;
    `,
  })}
`
